"use client";

import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { PdfViewer } from "./pdf-viewer";
import { DownloadBothMenu, InfoSheetSwitch, RegenerateSheetButton, useInfoSheet, type SheetView } from "./info-sheet";
import { SignatureBadge, storedFileIdOf } from "./signature-badge";
import { Dialog } from "@/components/ui/dialog";
import { ModalContent, ModalFooter, ModalHeader } from "@/components/forms/form-modal";

type ModalProps = { href: string; title: string; open: boolean; onOpenChange: (open: boolean) => void; verify?: boolean; startOnSheet?: boolean };

const sheetHref = (showing: SheetView, sheet: { id: string | null; evidenceId: string | null }) => {
  const id = showing === "evidence" ? sheet.evidenceId : showing === "sheet" ? sheet.id : null;
  return id ? `/api/documents/explorer/file/${id}` : null;
};

/** The big window that shows a PDF (`href` is its download address) instead of downloading it: the PDF in the browser's own reader under a toolbar (pages, zoom, print), and a footer with Download. A kept file that carries a signature shows its badge in the header (`verify` opens the details at once). Controlled, so a menu item can open it. */
export function PdfPreviewModal({ href, title, open, onOpenChange, verify = false, startOnSheet = false }: ModalProps) {
  const t = useTranslations("pdfPreview");
  const [showing, setShowing] = useState<SheetView>(startOnSheet ? "sheet" : "document");
  const sheet = useInfoSheet(href, open);
  const fileId = storedFileIdOf(href);
  const shown = sheetHref(showing, sheet) ?? href;
  const show = (next: SheetView) => { setShowing(next); if (next === "sheet" && sheet.available && !sheet.exact) void sheet.build(); if (next === "evidence" && !sheet.evidenceExact) void sheet.buildEvidence(); }; // the copy in this language is made when first asked for
  useEffect(() => { if (open && startOnSheet && sheet.available && !sheet.exact && !sheet.busy) void sheet.build(); }, [open, startOnSheet, sheet.available]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) setShowing(startOnSheet ? "sheet" : "document"); onOpenChange(next); }}>
      <ModalContent size="xl">
        <ModalHeader icon={<FileText />} title={title} description={t("help")} />
        {open && (
          <div className="flex flex-wrap items-center gap-2 border-b bg-muted/20 px-6 py-2">
            {sheet.available && <InfoSheetSwitch value={showing} onChange={show} evidence={!!sheet.evidenceId} />}
            {sheet.canRegenerate && <RegenerateSheetButton busy={sheet.busy} onClick={(language) => void (showing === "evidence" ? sheet.regenerateEvidence(language) : sheet.regenerate(language))} />}
            <div className="ms-auto min-w-0"><SignatureBadge href={href} openNow={verify} /></div>
          </div>
        )}
        {open && (showing === "sheet" && sheet.busy && !sheet.id ? <p role="status" className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground"><Loader2 className="animate-spin" aria-hidden />{t("sheet.building")}</p> : <PdfViewer href={shown} />)}
        <ModalFooter>
          {sheet.available && fileId && <DownloadBothMenu fileId={fileId} />}
          <Button type="button" variant="outline" nativeButton={false} render={<a href={shown} download />}><Download aria-hidden />{t("download")}</Button>
        </ModalFooter>
      </ModalContent>
    </Dialog>
  );
}

type ButtonProps = { href: string; title: string; variant?: "default" | "outline"; size?: "sm" | "default"; children: ReactNode };

/** A button that opens `PdfPreviewModal` for the PDF at `href`; `children` is its label (usually an icon and a word). */
export function PdfPreviewButton({ href, title, variant = "outline", size = "sm", children }: ButtonProps) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant={variant} size={size} onClick={() => setOpen(true)}>{children}</Button>
      <PdfPreviewModal href={href} title={title} open={open} onOpenChange={setOpen} />
    </>
  );
}

type FormProps = { action: string; title: string; className?: string; children: ReactNode };

/** A GET form that makes a PDF from its fields (the year, the language…): sending it opens `PdfPreviewModal` on the address the form would have gone to. `children` holds the fields and the submit button. */
export function PdfPreviewForm({ action, title, className, children }: FormProps) {
  const [href, setHref] = useState<string | null>(null);
  const show = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fields = new URLSearchParams(new FormData(event.currentTarget) as unknown as Record<string, string>);
    setHref(`${action}?${fields}`);
  };
  return (
    <>
      <form onSubmit={show} className={className}>{children}</form>
      {href && <PdfPreviewModal href={href} title={title} open onOpenChange={(open) => { if (!open) setHref(null); }} />}
    </>
  );
}
